/**
 * uploadDocument — single shared upload pipeline for the ADI client portal.
 *
 * Used by:
 *   - src/pages/Documents.tsx        (per-checklist-item uploads)
 *   - src/lib/schema-renderer/primitives/FileUploadR.tsx  (schema-driven file fields)
 *
 * Pipeline:
 *   1. Type/extension allowlist (PDF, JPG/JPEG, PNG, HEIC).
 *   2. HEIC -> JPEG conversion (lazy-loaded heic2any). Browsers can't render
 *      HEIC and Supabase Storage doesn't auto-convert.
 *   3. 5 MP downscale on images (PDFs untouched).
 *   4. Upload to Supabase Storage at:
 *        applicant: <matter_id>/<contact_id>/<checklist_item>/<uuid>.<ext>
 *        sponsor:   <matter_id>/sponsor/<contact_id>/<checklist_item>/<uuid>.<ext>
 *   5. Insert a `documents` row tagged with the checklist key + uploader_role.
 *
 * NOTE on bucket choice: this helper writes to BUCKET_INTAKE ('intake-documents')
 * which is provisioned by migration 20260507_intake_documents_bucket.sql.
 * The legacy `matter-documents` bucket is still readable for old rows.
 */

import { supabase, logAuditEvent } from './supabase';
import { isImpersonationPreview } from './impersonationPreview';

const ALLOWED_MIME = new Set<string>([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/heic',
  'image/heif',
  'application/pdf',
]);
// Exported so the Documents page copy (and its test) can stay in lockstep
// with the enforced allowlist.
export const ALLOWED_EXT = new Set<string>(['jpg', 'jpeg', 'png', 'heic', 'heif', 'pdf']);
/**
 * Human-readable accepted-formats list. Shared by the validation error below
 * AND the Documents page "File limits" card so the client-facing copy can
 * never drift from what validateType() actually accepts.
 */
export const ACCEPTED_FORMATS_LABEL = 'JPG, PNG, HEIC, or PDF';
const HEIC_EXT = new Set<string>(['heic', 'heif']);
const HEIC_MIME = new Set<string>(['image/heic', 'image/heif']);

const MAX_MEGAPIXELS = 5;
export const BUCKET_INTAKE = 'intake-documents';

export interface UploadDocumentArgs {
  file: File;
  matterId: string;
  contactId: string;
  /**
   * Stable checklist key (e.g. 'applicant_passport') OR a virtual key derived
   * from a schema canonical_name (see canonicalNameToChecklistKey()).
   */
  checklistItem: string;
  /**
   * Optional. Free-form document_type for cases where the upload is tagged by
   * a category rather than a checklist key. Defaults to checklistItem.
   */
  documentType?: string;
  /**
   * Optional. When this upload is replacing a previous version flagged as
   * `needs_replacement`, pass the old document id. The new row is linked
   * via `previous_version_id` and assigned `version_number = old + 1`.
   * The old row is preserved for compliance retention.
   */
  replacesDocumentId?: string;
  /**
   * Optional. Who is uploading this file — drives the storage path scheme
   * and the `documents.uploader_role` column. Defaults to 'applicant' to
   * preserve existing behaviour for every existing caller. The caller MUST
   * pass the role explicitly; this helper does NOT auto-detect from the
   * contact (a staff member may upload on behalf of either side).
   */
  uploaderRole?: 'applicant' | 'sponsor';
}

export interface UploadDocumentResult {
  documentId: string;
  storagePath: string;
  filename: string;
  sizeBytes: number;
  mimeType: string;
  /** Pixel/byte stats for the UI ("compressed from X to Y"). */
  compression: { from: number; to: number } | null;
  /** True if HEIC was converted to JPEG before upload. */
  heicConverted: boolean;
}

export class UploadValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UploadValidationError';
  }
}

/**
 * canonical_name -> checklist key mapping.
 *
 * Schema canonical_names are dotted paths (e.g. 'applicant.passport.scan').
 * The portal stores documents keyed by checklistItem (e.g. 'applicant_passport').
 *
 * Rule: dots become underscores, [*] indices are stripped. This is reversible
 * enough that a staff query "all uploads for applicant_passport" matches both
 * checklist uploads and schema-driven file fields.
 */
export function canonicalNameToChecklistKey(canonicalName: string): string {
  return canonicalName
    .replace(/\[\*\]/g, '')
    .replace(/\[\d+\]/g, '')
    .replace(/\./g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
}

/**
 * Run the full upload pipeline. Returns the inserted documents row's id +
 * storage path, suitable for embedding in a form value or appending to a UI list.
 */
export async function uploadDocument(args: UploadDocumentArgs): Promise<UploadDocumentResult> {
  const { matterId, contactId, checklistItem, documentType, replacesDocumentId } = args;
  const uploaderRole: 'applicant' | 'sponsor' = args.uploaderRole ?? 'applicant';
  let file = args.file;

  // 1. Type allowlist on the original file.
  validateType(file);

  // 2. HEIC -> JPEG (browsers and Supabase Storage previews can't render HEIC).
  let heicConverted = false;
  if (isHeic(file)) {
    file = await convertHeicToJpeg(file);
    heicConverted = true;
  }

  // 3. 5 MP downscale on images.
  let compression: UploadDocumentResult['compression'] = null;
  if (file.type.startsWith('image/')) {
    const { downscaleToMegapixels } = await import('./compressImage');
    const result = await downscaleToMegapixels(file, MAX_MEGAPIXELS);
    if (result && result.compressed) {
      compression = { from: result.originalBytes, to: result.finalBytes };
      file = result.file;
    }
  }

  // 4. Upload to Storage.
  const ext = (file.name.split('.').pop() ?? '').toLowerCase() || mimeToExt(file.type) || 'bin';
  const objectId = crypto.randomUUID();
  // Sponsors get an extra `/sponsor/` segment after the matter id so storage
  // RLS can isolate sponsor uploads from the applicant's tree without
  // inspecting any DB row. The first segment stays matter_id (preserves the
  // intake_matter_id_from_path() helper used by storage policies).
  const path = uploaderRole === 'sponsor'
    ? `${matterId}/sponsor/${contactId}/${checklistItem}/${objectId}.${ext}`
    : `${matterId}/${contactId}/${checklistItem}/${objectId}.${ext}`;
  const { error: uploadErr } = await supabase.storage
    .from(BUCKET_INTAKE)
    .upload(path, file, {
      cacheControl: '3600',
      upsert: false,
      contentType: file.type || 'application/octet-stream',
    });
  if (uploadErr) throw uploadErr;

  // If this is a replacement, look up the old version's chain position so the
  // new row gets the next version_number. Old row stays untouched (compliance
  // retention; staff DocumentViewer walks the chain via previous_version_id).
  let nextVersionNumber = 1;
  if (replacesDocumentId) {
    const { data: prev, error: prevErr } = await supabase
      .from('documents')
      .select('version_number, matter_id')
      .eq('id', replacesDocumentId)
      .maybeSingle();
    if (prevErr) {
      await supabase.storage.from(BUCKET_INTAKE).remove([path]).catch(() => {});
      throw prevErr;
    }
    if (!prev || prev.matter_id !== matterId) {
      await supabase.storage.from(BUCKET_INTAKE).remove([path]).catch(() => {});
      throw new UploadValidationError('Replacement target not found on this matter.');
    }
    nextVersionNumber = (prev.version_number ?? 1) + 1;
  }

  // 5. Insert documents row. RLS on `documents` ensures the caller can only
  // insert rows on matters they're linked to.
  // `uploader_role` is added by migration 20260508000002_sponsor_documents.sql
  // but isn't yet present in the generated database.types.ts. Cast around it
  // so the value is sent to PostgREST without losing static typing on the
  // rest of the row. (Same pattern the codebase uses for other recently-added
  // columns — see reviewer_staff_id in MatterOverview.tsx.)
  const insertPayload = {
    matter_id: matterId,
    contact_id: contactId,
    storage_path: path,
    filename: file.name,
    mime_type: file.type || null,
    size_bytes: file.size,
    checklist_item: checklistItem,
    document_type: documentType ?? checklistItem,
    previous_version_id: replacesDocumentId ?? null,
    version_number: nextVersionNumber,
    uploader_role: uploaderRole,
  };
  const { data: inserted, error: insertErr } = await supabase
    .from('documents')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .insert(insertPayload as any)
    .select('id, storage_path, filename, mime_type, size_bytes')
    .single();
  if (insertErr) {
    // If the documents row insert fails, the storage object becomes orphaned.
    // Best-effort cleanup so we don't accumulate garbage on validation errors.
    await supabase.storage.from(BUCKET_INTAKE).remove([path]).catch(() => {});
    throw insertErr;
  }

  // Staff uploading from inside "view as client" land on the client's own
  // checklist and are otherwise indistinguishable from the client's own
  // upload. Record who actually did it (see upsertIntakeFieldData for the
  // reasoning). Best-effort: a failed log must never fail the upload.
  if (isImpersonationPreview()) {
    void logAuditEvent('staff_edit_as_client', {
      tableName: 'documents',
      rowId: inserted.id,
      changes: { filename: inserted.filename },
    }).catch(() => {
      /* ignore */
    });
  }

  // NOTE (Batch 4C item 2, Jane 2026-07-21): the Drive mirror is NO LONGER
  // kicked on upload. Client uploads sit in a pending review state; the file is
  // copied to the live Drive folder only when staff APPROVE it (see
  // DocumentViewer's status-change handler, which calls driveSync.kick). This
  // keeps unreviewed uploads out of the ADI shared drive.

  return {
    documentId: inserted.id,
    storagePath: inserted.storage_path,
    filename: inserted.filename,
    sizeBytes: inserted.size_bytes ?? file.size,
    mimeType: inserted.mime_type ?? file.type ?? 'application/octet-stream',
    compression,
    heicConverted,
  };
}

function validateType(file: File): void {
  const ext = (file.name.split('.').pop() ?? '').toLowerCase();
  const mimeOk = file.type ? ALLOWED_MIME.has(file.type) : false;
  const extOk = ALLOWED_EXT.has(ext);
  if (!mimeOk && !extOk) {
    throw new UploadValidationError(
      `Unsupported file type. Use ${ACCEPTED_FORMATS_LABEL}.`,
    );
  }
}

function isHeic(file: File): boolean {
  if (HEIC_MIME.has(file.type)) return true;
  const ext = (file.name.split('.').pop() ?? '').toLowerCase();
  return HEIC_EXT.has(ext);
}

async function convertHeicToJpeg(file: File): Promise<File> {
  // Lazy-loaded so the ~250 KB heic2any blob only ships when needed.
  const { default: heic2any } = await import('heic2any');
  const out = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.85 });
  const blob = Array.isArray(out) ? out[0] : out;
  const newName = file.name.replace(/\.[^.]+$/, '') + '.jpg';
  return new File([blob], newName, { type: 'image/jpeg', lastModified: Date.now() });
}

function mimeToExt(mime: string): string | null {
  switch (mime) {
    case 'image/jpeg':
    case 'image/jpg':
      return 'jpg';
    case 'image/png':
      return 'png';
    case 'image/heic':
      return 'heic';
    case 'image/heif':
      return 'heif';
    case 'application/pdf':
      return 'pdf';
    default:
      return null;
  }
}
